import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    Tooltip,
    Grid,
    Card,
    CardContent,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    ToggleOn as ToggleIcon,
    AttachMoney as MoneyIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import { planAPI } from '../../utils/planAPI';

const PlanList = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planStats, setPlanStats] = useState({});

    // Form state
    const [planForm, setPlanForm] = useState({
        name: '',
        description: '',
        price_monthly: 0,
        price_yearly: 0,
        quota_limit: 0,
        features: {
            content_generation: false,
            image_creation: false,
            website_creation: false,
            blog_generation: false,
            seo_optimization: false
        },
        is_active: true
    });

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            setLoading(true);
            const response = await planAPI.getAllPlans();
            setPlans(response.data);

            // Fetch stats for each plan
            const statsPromises = response.data.map(plan =>
                planAPI.getPlanUsageStats(plan.id).catch(() => null)
            );
            const statsResults = await Promise.all(statsPromises);
            const statsMap = {};
            statsResults.forEach((result, index) => {
                if (result) {
                    statsMap[response.data[index].id] = result.data;
                }
            });
            setPlanStats(statsMap);
        } catch (error) {
            setError('Failed to fetch plans: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePlan = async () => {
        try {
            setLoading(true);
            await planAPI.createPlan(planForm);
            setSuccess('Plan created successfully!');
            setOpenDialog(false);
            resetForm();
            fetchPlans();
        } catch (error) {
            setError('Failed to create plan: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdatePlan = async () => {
        try {
            setLoading(true);
            await planAPI.updatePlan(selectedPlan.id, planForm);
            setSuccess('Plan updated successfully!');
            setOpenDialog(false);
            setSelectedPlan(null);
            resetForm();
            fetchPlans();
        } catch (error) {
            setError('Failed to update plan: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDeletePlan = async (planId) => {
        if (window.confirm('Are you sure you want to delete this plan?')) {
            try {
                setLoading(true);
                await planAPI.deletePlan(planId);
                setSuccess('Plan deleted successfully!');
                fetchPlans();
            } catch (error) {
                setError('Failed to delete plan: ' + (error.response?.data?.detail || error.message));
            } finally {
                setLoading(false);
            }
        }
    };

    const handleToggleStatus = async (planId) => {
        try {
            setLoading(true);
            await planAPI.togglePlanStatus(planId);
            setSuccess('Plan status updated successfully!');
            fetchPlans();
        } catch (error) {
            setError('Failed to update plan status: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const openEditPlan = (plan) => {
        setSelectedPlan(plan);
        setPlanForm({
            name: plan.name,
            description: plan.description || '',
            price_monthly: plan.price_monthly,
            price_yearly: plan.price_yearly,
            quota_limit: plan.quota_limit,
            features: plan.features || {},
            is_active: plan.is_active
        });
        setOpenDialog(true);
    };

    const openCreatePlan = () => {
        setSelectedPlan(null);
        resetForm();
        setOpenDialog(true);
    };

    const resetForm = () => {
        setPlanForm({
            name: '',
            description: '',
            price_monthly: 0,
            price_yearly: 0,
            quota_limit: 0,
            features: {
                content_generation: false,
                image_creation: false,
                website_creation: false,
                blog_generation: false,
                seo_optimization: false
            },
            is_active: true
        });
    };

    const handleFeatureChange = (feature) => {
        setPlanForm(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [feature]: !prev.features[feature]
            }
        }));
    };

    if (loading && plans.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Subscription Plans Management
            </Typography>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Plans Grid */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {plans.map((plan) => (
                    <Grid item xs={12} md={6} lg={4} key={plan.id}>
                        <Card>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            {plan.name}
                                        </Typography>
                                        <Chip
                                            label={plan.is_active ? 'Active' : 'Inactive'}
                                            color={plan.is_active ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="h5" color="primary">
                                            ${plan.price_monthly}/mo
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            ${plan.price_yearly}/year
                                        </Typography>
                                    </Box>
                                </Box>

                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    {plan.description}
                                </Typography>

                                {/* Plan Stats */}
                                {planStats[plan.id] && (
                                    <Box sx={{ mb: 2 }}>
                                        <Grid container spacing={1}>
                                            <Grid item xs={6}>
                                                <Box display="flex" alignItems="center">
                                                    <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                                                    <Typography variant="body2">
                                                        {planStats[plan.id].active_subscriptions} active
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box display="flex" alignItems="center">
                                                    <MoneyIcon fontSize="small" sx={{ mr: 1 }} />
                                                    <Typography variant="body2">
                                                        ${planStats[plan.id].estimated_monthly_revenue}/mo
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}

                                {/* Features */}
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Features:
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {Object.entries(plan.features || {}).map(([feature, enabled]) => (
                                            <Chip
                                                key={feature}
                                                label={feature.replace('_', ' ')}
                                                size="small"
                                                color={enabled ? 'primary' : 'default'}
                                                variant={enabled ? 'filled' : 'outlined'}
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                {/* Actions */}
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Tooltip title="Edit Plan">
                                            <IconButton
                                                size="small"
                                                onClick={() => openEditPlan(plan)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Toggle Status">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleStatus(plan.id)}
                                            >
                                                <ToggleIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Plan">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDeletePlan(plan.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary">
                                        Quota: {plan.quota_limit}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Create Plan Button */}
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreatePlan}
                disabled={loading}
                sx={{ mb: 2 }}
            >
                Create New Plan
            </Button>

            {/* Plan Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle>
                    {selectedPlan ? 'Edit Plan' : 'Create New Plan'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Plan Name"
                                value={planForm.name}
                                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Quota Limit"
                                type="number"
                                value={planForm.quota_limit}
                                onChange={(e) => setPlanForm({ ...planForm, quota_limit: parseInt(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                value={planForm.description}
                                onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Monthly Price ($)"
                                type="number"
                                value={planForm.price_monthly}
                                onChange={(e) => setPlanForm({ ...planForm, price_monthly: parseFloat(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Yearly Price ($)"
                                type="number"
                                value={planForm.price_yearly}
                                onChange={(e) => setPlanForm({ ...planForm, price_yearly: parseFloat(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Features
                            </Typography>
                            <Grid container spacing={2}>
                                {Object.keys(planForm.features).map((feature) => (
                                    <Grid item xs={12} sm={6} key={feature}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={planForm.features[feature]}
                                                    onChange={() => handleFeatureChange(feature)}
                                                />
                                            }
                                            label={feature.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={planForm.is_active}
                                        onChange={(e) => setPlanForm({ ...planForm, is_active: e.target.checked })}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={selectedPlan ? handleUpdatePlan : handleCreatePlan}
                        variant="contained"
                        disabled={loading}
                    >
                        {selectedPlan ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PlanList; 